/* @flow */

import * as React from 'react';

import Localizer from '../../../helpers/localization';
import { FEATURE_TV, type FEATURE_TYPE } from '../../../redux/appConf/constants';

type ShortcutTrigger = {
  altKey?: boolean,
  ctrlKey?: boolean,
  key: string,
  shiftKey?: boolean,
};

export type Shortcut = {
  action: string,
  feature?: FEATURE_TYPE,
  id: string,
  trigger: ShortcutTrigger,
};

export const PLAYER_SHORTCUT_LIST: Array<Shortcut> = Object.freeze([
  {
    action: 'modal.keyboard_shortcuts.toggle_play',
    id: 'ptp',
    trigger: { key: 'key.space' },
  },
  {
    action: 'modal.keyboard_shortcuts.rewind_10seconds',
    id: 'prw10',
    trigger: { key: 'LEFT_ARROW' },
  },
  {
    action: 'modal.keyboard_shortcuts.rewind_30seconds',
    id: 'prw30',
    trigger: {
      ctrlKey: true,
      key: 'LEFT_ARROW',
    },
  },
  {
    action: 'modal.keyboard_shortcuts.rewind_60seconds',
    id: 'prw60',
    trigger: {
      key: 'LEFT_ARROW',
      shiftKey: true,
    },
  },
  {
    action: 'modal.keyboard_shortcuts.forward_10seconds',
    id: 'pfw10',
    trigger: { key: 'RIGHT_ARROW' },
  },
  {
    action: 'modal.keyboard_shortcuts.forward_30seconds',
    id: 'pfw30',
    trigger: {
      ctrlKey: true,
      key: 'RIGHT_ARROW',
    },
  },
  {
    action: 'modal.keyboard_shortcuts.forward_60seconds',
    id: 'pfw60',
    trigger: {
      key: 'RIGHT_ARROW',
      shiftKey: true,
    },
  },
  {
    action: 'modal.keyboard_shortcuts.toggle_sound',
    id: 'pts',
    trigger: { key: 'M' },
  },
  {
    action: 'modal.keyboard_shortcuts.decrease_volume',
    id: 'pdv',
    trigger: { key: '-' },
  },
  {
    action: 'modal.keyboard_shortcuts.increase_volume',
    id: 'piv',
    trigger: { key: '+' },
  },
  {
    action: 'modal.keyboard_shortcuts.toggle_fullscreen',
    id: 'ptf',
    trigger: { key: 'F' },
  },
  {
    action: 'modal.keyboard_shortcuts.show_info',
    id: 'psi',
    trigger: { key: 'I' },
  },
  {
    action: 'modal.keyboard_shortcuts.hide_controller',
    id: 'phc',
    trigger: { key: 'H' },
  },
  {
    action: 'modal.keyboard_shortcuts.close_player',
    id: 'pcp',
    trigger: { key: 'key.escape' },
  },
  {
    action: 'modal.keyboard_shortcuts.previous_channel',
    feature: FEATURE_TV,
    id: 'ppc',
    trigger: { key: 'DOWN_ARROW' },
  },
  {
    action: 'modal.keyboard_shortcuts.next_channel',
    feature: FEATURE_TV,
    id: 'pnc',
    trigger: { key: 'UP_ARROW' },
  },
  {
    action: 'modal.keyboard_shortcuts.back_to_live',
    feature: FEATURE_TV,
    id: 'pbl',
    trigger: { key: 'L' },
  },
]);

export const TRAILER_SHORTCUT_LIST: Array<Shortcut> = Object.freeze([
  {
    action: 'modal.keyboard_shortcuts.toggle_play',
    id: 'ttp',
    trigger: { key: 'key.space' },
  },
  {
    action: 'modal.keyboard_shortcuts.rewind_10seconds',
    id: 'trw',
    trigger: { key: 'LEFT_ARROW' },
  },
  {
    action: 'modal.keyboard_shortcuts.forward_10seconds',
    id: 'tfw',
    trigger: { key: 'RIGHT_ARROW' },
  },
  {
    action: 'modal.keyboard_shortcuts.toggle_sound',
    id: 'tts',
    trigger: { key: 'M' },
  },
  {
    action: 'modal.keyboard_shortcuts.toggle_fullscreen',
    id: 'ttf',
    trigger: { key: 'F' },
  },
  {
    action: 'modal.keyboard_shortcuts.exit_fullscreen',
    id: 'tcp',
    trigger: { key: 'key.escape' },
  },
]);

const getKeyRepresentation = (key: string): { ariaLabel: string, display: string } => {
  switch (key) {
    case 'DOWN_ARROW':
      return { ariaLabel: Localizer.localize('accessibility.modal.keyboard_shortcuts_down_arrow'), display: '\u2193' };
    case 'LEFT_ARROW':
      return { ariaLabel: Localizer.localize('accessibility.modal.keyboard_shortcuts_left_arrow'), display: '\u2190' };
    case 'RIGHT_ARROW':
      return { ariaLabel: Localizer.localize('accessibility.modal.keyboard_shortcuts_right_arrow'), display: '\u2192' };
    case 'UP_ARROW':
      return { ariaLabel: Localizer.localize('accessibility.modal.keyboard_shortcuts_up_arrow'), display: '\u2191' };
    default: {
      const localizedKey = Localizer.localize(key);
      return { ariaLabel: key === '-' ? Localizer.localize('accessibility.modal.keyboard_shortcuts_minus') : localizedKey.toLowerCase(), display: localizedKey !== key ? localizedKey : key };
    }
  }
};

const renderShortcutTrigger = (trigger: ShortcutTrigger): React.Node => {
  const { altKey, ctrlKey, key, shiftKey } = trigger;

  const keys = [];
  if (altKey) {
    keys.push({ ariaLabel: 'Alt', display: 'Alt' });
  }
  if (ctrlKey) {
    keys.push({ ariaLabel: 'Ctrl', display: 'Ctrl' });
  }
  if (shiftKey) {
    keys.push({ ariaLabel: 'Shift', display: 'Shift' });
  }

  keys.push(getKeyRepresentation(key));

  return (
    <>
      {keys.map(({ ariaLabel, display }) => (
        <kbd key={display}>
          <span className='screenReaderOnly'>{ariaLabel}</span>
          <span aria-hidden>{display}</span>
        </kbd>
      ))}
    </>
  );
};

export { renderShortcutTrigger };
